import React, { useMemo } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import Logos from "../../../assets/images/logo.svg";
import { useSelector } from "react-redux";

interface PropsData {
  plan_name: string;
  client_secret: any;
  payment_intent: string;
  trn_id: string;
  trans_amount: string;
  exchange_rate: string;
  currency_code: string;
  is_test_account?: boolean | number | string;
  stripe_key?: string;
  secret_key?: string;
  onSuccess: () => void;
}

const Payment = ({
  plan_name,
  client_secret,
  payment_intent,
  trn_id,
  trans_amount,
  exchange_rate,
  currency_code,
  is_test_account: propIsTestAccount,
  stripe_key,
  secret_key,
  onSuccess
}: PropsData) => {
  let profileDetails = useSelector((state: any) => state?.items?.profileDetail);

  let rawTestVal =
    propIsTestAccount !== undefined && propIsTestAccount !== null
      ? propIsTestAccount
      : profileDetails?.is_test_account ?? false;

  const is_test_account =
    rawTestVal === true ||
    rawTestVal === 1 ||
    rawTestVal === "1" ||
    rawTestVal === "true" ||
    Number(rawTestVal) === 1;

  const STRIPE_PUBLIC_KEY = (
    stripe_key ||
    secret_key ||
    (is_test_account
      ? process.env.REACT_APP_PUBLIC_STRIPE_KEY_TEST
      : process.env.REACT_APP_PUBLIC_STRIPE_KEY) ||
    ""
  ).trim();

  const stripePromise = useMemo(() => {
    return STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;
  }, [STRIPE_PUBLIC_KEY]);

  if (!client_secret) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <div className="mt-2 text-muted" style={{ fontSize: 13 }}>
          Initializing payment…
        </div>
      </div>
    );
  }

  return (
    <div className="position-relative">
      <Container>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <img
            src={Logos}
            alt={"Prime shark"}
            style={{ height: 28 }}
          />
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              color: "hsl(215, 16%, 47%)",
            }}
            title="Secure checkout powered by Stripe"
          >
            <span aria-hidden>🔒</span>
            <span>Secure • Powered by Stripe</span>
          </div>
        </div>

        {/* Order Summary */}
        <div
          style={{
            border: "1px solid hsl(214, 32%, 91%)",
            borderRadius: 16,
            padding: 16,
            background: "hsl(0 0% 100%)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 6,
              columnGap: 8,
              fontSize: 14,
            }}
          >
            <span style={{ color: "hsl(215, 16%, 47%)" }}>Item</span>
            <span style={{ justifySelf: "end", color: "hsl(215, 16%, 47%)" }}>
              Amount
            </span>
            <div>
              <strong>
                <span>Subscription Payment</span>
              </strong>
              <br />
              <span className="text-muted">Plan Name:</span>{" "}
              <span className="font-bold">{plan_name || "Membership"}</span>
            </div>
            <strong className="fw-bold" style={{ justifySelf: "end" }}>
              {currency_code} {Math.round(Number(trans_amount) * Number(exchange_rate))}
            </strong>
            <span style={{ color: "hsl(215, 16%, 47%)" }}>Taxes & fees</span>
            <span style={{ justifySelf: "end", color: "hsl(215, 16%, 47%)" }}>
              Included
            </span>
            <div
              style={{
                gridColumn: "1 / -1",
                height: 1,
                background: "hsl(214, 32%, 91%)",
                margin: "6px 0",
              }}
            />
            <span className="fw-bold text-success" style={{ fontWeight: 700 }}>Total</span>
            <span className="fw-bold text-success" style={{ justifySelf: "end", fontWeight: 800 }}>
              {currency_code} {Math.round(Number(trans_amount) * Number(exchange_rate))}
            </span>
          </div>
        </div>

        <Row>
          <Col className="mx-auto">
            {stripePromise && client_secret && payment_intent && trn_id ? (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: client_secret }}
                key={client_secret}
              >
                <CheckoutForm
                  paymentIntent={payment_intent}
                  trnsId={trn_id ?? ""}
                  onSuccess={onSuccess}
                />
              </Elements>
            ) : !stripePromise ? (
              <div className="alert alert-danger text-center my-3" role="alert">
                Stripe publishable key is missing. Please verify your configuration.
              </div>
            ) : (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading checkout…</span>
              </div>
            )}
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            justifyContent: "center",
            marginTop: 8,
            fontSize: 12,
            color: "hsl(215, 16%, 47%)",
          }}
        >
          <span aria-hidden>🛡️</span>
          <span>PCI-DSS compliant • 3D Secure supported</span>
        </div>
      </Container>
    </div>
  );
};

export default Payment;
